import { Dispatch, forwardRef, JSX, SetStateAction, useImperativeHandle } from 'react';
import type IValidatable from '../validatable.interface';
import ValidationInfo from '../../../validation/validation-info';
import ValidationEngine from '../../../validation/validation-engine';
import type { IServerValidationError } from '../validatable.interface';
import RequiredAsterisk from '../RequiredAsterisk';

export interface INumberFieldFormGroupState {
  value: number | undefined;
  validationInfo: ValidationInfo;
}

export interface INumberFieldFormGroupProps {
  inputId: string;
  className?: string;
  serverValidationName?: string;
  labelText: string;
  ariaDescribedby?: string;
  placeholder?: string;
  required?: boolean;
  allowDecimals?: boolean;
  min?: number;
  max?: number;
  step?: number;
  helpTextElement?: JSX.Element;
  state: INumberFieldFormGroupState | [INumberFieldFormGroupState, Dispatch<SetStateAction<INumberFieldFormGroupState>>];
  onChange?: (value: number | undefined) => void;
  onKeyUp?: () => void;
}

export default forwardRef<IValidatable, INumberFieldFormGroupProps>(function NumberFieldFormGroup(
  {
    inputId,
    className,
    serverValidationName = undefined,
    labelText,
    ariaDescribedby = undefined,
    placeholder = undefined,
    required = false,
    allowDecimals = false,
    min = undefined,
    max = 2147483647, // Max value for a 32-bit signed integer
    step = undefined,
    helpTextElement = undefined,
    state,
    onChange = undefined,
    onKeyUp = undefined,
  }: INumberFieldFormGroupProps,
  ref
) {
  const errorTextId = `${inputId}-error`;
  const helpTextId = helpTextElement ? `${inputId}-help` : undefined;
  const stateDerived = Array.isArray(state) ? state[0] : state;

  // === HELPER FUNCTIONS ===
  function createAriaDescribedBy(): string | undefined {
    if (!stateDerived.validationInfo.isInvalid && !ariaDescribedby && !helpTextElement) return undefined;
    return `${stateDerived.validationInfo.isInvalid ? errorTextId : ''} ${ariaDescribedby ?? ''} ${helpTextId ?? ''}`.trim().replace('  ', ' ');
  }

  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      const updatedValidationInfo = new ValidationInfo(`${labelText} - `);
      ValidationEngine.for(updatedValidationInfo).requiredIf(required, stateDerived.value).validate();
      return updatedValidationInfo;
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo(
        `${labelText} - `,
        errors
          .filter((e) => e.name === serverValidationName)
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), [])
      );
    },
  }));

  return (
    <div className={`mb-3 ${className ?? ''}`}>
      <label htmlFor={inputId} className="form-label">
        {labelText}
        {required && <RequiredAsterisk />}
      </label>
      <input
        id={inputId}
        value={stateDerived.value !== undefined ? stateDerived.value : ''}
        type="number"
        inputMode={allowDecimals ? 'decimal' : 'numeric'}
        placeholder={placeholder}
        className={`form-control ${stateDerived.validationInfo.isInvalid ? 'is-invalid' : ''}`}
        aria-describedby={createAriaDescribedBy()}
        aria-invalid={stateDerived.validationInfo.isInvalid ? 'true' : 'false'}
        required={required}
        min={min}
        max={max}
        step={step}
        onChange={(e) => (Array.isArray(state) ? state[1]({ ...state[0], value: Number(e.target.value) }) : onChange?.(e.target.value !== '' ? Number(e.target.value) : undefined))}
        onKeyUp={() => (Array.isArray(state) ? state[1]({ ...state[0], validationInfo: new ValidationInfo() }) : onKeyUp?.())}
      />
      {stateDerived.validationInfo.isInvalid && (
        <div id={errorTextId} className="invalid-feedback">
          {stateDerived.validationInfo.getErrors().map((error) => (
            <div key={error}>{error}</div>
          ))}
        </div>
      )}
      {helpTextElement && (
        <div className="form-text">
          <div id={helpTextId}>{helpTextElement}</div>
        </div>
      )}{' '}
    </div>
  );
});
